/**
 * Mg Theme — Quiz state machine
 * (c) 2026 NAMY SOLUTIONS LLC
 *
 * Custom element <mg-quiz> orchestrating a single-question-per-screen
 * product recommendation quiz. Pre-rendered server-side via Liquid;
 * JS only handles state, navigation, persistence, ATC chain, events.
 *
 * DRY discipline :
 *   - Mg.cart.{add,update,get}      — cart Ajax (NEVER manual fetch)
 *   - Mg.debug                       — gated logging (NEVER console.error)
 *   - Mg.debounce                    — debounce timer (NEVER setTimeout)
 *   - Mg.section.fetch/render        — Section Rendering API
 *   - MgEvents.publish + .track      — internal bus + GA4/Meta/PostHog fan-out
 *   - MgEvents.Events.QUIZ_*         — frozen string constants
 *
 * Persistence: localStorage keyed by section.id + cart attributes after ATC.
 * State key on each block = block.id (Shopify-stable; never forloop.index).
 * Source filter on dispatched namy:cart:updated to prevent loops.
 */
if (!customElements.get('mg-quiz')) {
  class MgQuiz extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.sectionId = this.dataset.sectionId;
      this.steps = [...this.querySelectorAll('[data-mg-quiz-step]')];
      this.questionSteps = this.steps.filter(function (s) { return s.dataset.questionId; });
      this.resultStep = this.querySelector('[data-quiz-result]');

      if (!this.questionSteps.length) return;

      // Parse JSON config — single blob at section root (~5MB/s parse on mobile).
      try {
        var cfgEl = this.querySelector('[data-quiz-config]');
        if (cfgEl) this.config = JSON.parse(cfgEl.textContent);
      } catch (e) { Mg.debug('Quiz:configParse', e); }
      this.config = this.config || {};
      this.locale = this.config.locale || {};

      // State restore — always-on (no merchant toggle; localStorage save & resume
      // is universal best practice and zero-cost when unused).
      this.state = this._restoreState() || this._freshState();
      this._persistDebounced = Mg.debounce(this._persistState.bind(this), 150);

      // Auto-derive resolution mode: if any answer carries a non-default
      // score weight (≠ 1), the merchant signaled intent to use score mode.
      // Otherwise direct mode (per-answer product mapping). Eliminates the
      // confusing "what's a resolution mode?" merchant decision.
      var hasCustomWeight = [...this.querySelectorAll('[data-mg-quiz-answer]')]
        .some(function (el) { return Number(el.dataset.score) !== 1; });
      this._resolutionMode = hasCustomWeight ? 'score' : 'direct';

      this._attachAnswerHandlers();
      this._attachNavHandlers();
      this._attachEmailHandlers();
      this._attachExternalListeners();
      this._attachPopupHandlers();

      this._render();

      // Restore result UI when state was persisted with a completed run.
      // Without this, _render() unhides the result step but the inner DOM
      // still shows the Liquid placeholder ("loading…") because the
      // _renderReasoning / _renderBullets / _renderHero pipeline only ever
      // fires from _complete() (user-driven path). Returning visitors with
      // a saved completion would otherwise see a broken empty result.
      if (this.state.showingResult) {
        this._renderReasoning();
        this._renderBullets();
        this._renderHero(this.state.matchHandle);
        if (this.state.altHandle) this._renderAlt(this.state.altHandle);
      }

      MgEvents.publish(MgEvents.Events.QUIZ_STARTED, {
        runId: this.state.runId, sectionId: this.sectionId
      });
      MgEvents.track('quiz:started', { run_id: this.state.runId });
    }

    disconnectedCallback() {
      clearTimeout(this._autoAdvanceTimer);
      if (this._boundCartUpdate) {
        document.removeEventListener('namy:cart:updated', this._boundCartUpdate);
      }
      if (this._boundEditorSelect) {
        document.removeEventListener('shopify:block:select', this._boundEditorSelect);
      }
      // Popup listeners cleanup (avoid ghost handlers on editor section reload)
      if (this._dialog && this._boundPopupBackdrop) {
        this._dialog.removeEventListener('click', this._boundPopupBackdrop);
      }
      if (this._popupTrigger && this._boundPopupOpen) {
        this._popupTrigger.removeEventListener('click', this._boundPopupOpen);
      }
      if (this._popupCloseBtn && this._boundPopupClose) {
        this._popupCloseBtn.removeEventListener('click', this._boundPopupClose);
      }
    }

    /* -----------------------------------------------------------------
       State
       ----------------------------------------------------------------- */

    _freshState() {
      return {
        runId: this._uuid(),
        currentStep: 0,
        answers: {},          // { questionId: { answerId, reasoningPhrase, matchReasonPhrase, productHandles, score } }
        showingResult: false
      };
    }

    _restoreState() {
      // localStorage save & resume is always-on (no merchant toggle).
      try {
        var raw = localStorage.getItem(this._stateKey());
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (!parsed || !parsed.runId) return null;

        // A saved run outlives the quiz it was taken on. Add or remove a question
        // and the stored index points past the end: every step then stays hidden
        // and the visitor meets a blank screen with no way forward. Answers can
        // also name questions that no longer exist. Keep what still resolves.
        var questionCount = this.querySelectorAll('[data-mg-quiz-step][data-question-id]').length;
        if (!questionCount) return null;

        var step = Number(parsed.currentStep);
        if (!isFinite(step) || step < 0) step = 0;
        parsed.currentStep = Math.min(step, questionCount - 1);

        if (parsed.answers && typeof parsed.answers === 'object') {
          var live = {};
          var self = this;
          Object.keys(parsed.answers).forEach(function (questionId) {
            if (self.querySelector('[data-question-id="' + questionId + '"]')) {
              live[questionId] = parsed.answers[questionId];
            }
          });
          parsed.answers = live;
        }

        return parsed;
      } catch (e) { Mg.debug('Quiz:restore', e); return null; }
    }

    _persistState() {
      try {
        localStorage.setItem(this._stateKey(), JSON.stringify(this.state));
      } catch (e) { Mg.debug('Quiz:persist', e); }
    }

    _stateKey() { return 'namy:quiz:' + this.sectionId + ':state'; }

    _uuid() {
      // RFC4122 v4-ish (good enough for run IDs, not crypto)
      return 'q-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    /* -----------------------------------------------------------------
       Handlers
       ----------------------------------------------------------------- */

    _attachAnswerHandlers() {
      var self = this;
      this.questionSteps.forEach(function (step) {
        var questionId = step.dataset.questionId;
        var answers = [].slice.call(step.querySelectorAll('[data-mg-quiz-answer]'));
        answers.forEach(function (answer) {
          var input = answer.querySelector('input[type="radio"]');
          // Listen to the radio's change event ONLY. Adding a click listener
          // on the wrapping <label> would double-fire because clicking a
          // label triggers both the label's click event AND the linked
          // input's change event — causing two _selectAnswer / _next calls
          // per user action and skipping a question.
          if (input) {
            input.addEventListener('change', function () {
              if (input.checked) self._selectAnswer(questionId, answer);
            });
          }
        });
      });
    }

    _attachNavHandlers() {
      var self = this;
      var nextBtn = this.querySelector('[data-quiz-next]');
      var prevBtn = this.querySelector('[data-quiz-prev]');
      var skipBtn = this.querySelector('[data-quiz-skip]');
      var atcBtn = this.querySelector('[data-quiz-atc]');
      var retakeBtn = this.querySelector('[data-quiz-retake]');
      var shareBtn = this.querySelector('[data-quiz-share]');

      if (nextBtn) nextBtn.addEventListener('click', function () { self._next(); });
      if (prevBtn) prevBtn.addEventListener('click', function () { self._prev(); });
      if (skipBtn) skipBtn.addEventListener('click', function () { self._next(true); });
      if (atcBtn) atcBtn.addEventListener('click', function () { self._handleCta(); });
      if (retakeBtn) retakeBtn.addEventListener('click', function () { self._retake(); });
      if (shareBtn) shareBtn.addEventListener('click', function () { self._share(); });
    }

    /* -----------------------------------------------------------------
       Popup mode — native <dialog> showModal/close lifecycle
       ----------------------------------------------------------------- */

    _attachPopupHandlers() {
      // Quiz lives inside .mg-quiz__dialog only when display_mode='popup'.
      // closest() walks up; if no dialog ancestor, popup mode is off.
      this._dialog = this.closest('.mg-quiz__dialog') || null;
      if (!this._dialog) return;

      var self = this;
      // Trigger button is a sibling of the dialog (rendered before <dialog> in section).
      // Scope to the same .mg-quiz-section to support multiple quiz instances.
      var section = this.closest('.mg-quiz-section');
      var trigger = section && section.querySelector('[data-mg-quiz-trigger]');
      var closeBtn = this._dialog.querySelector('[data-mg-quiz-close]');

      this._boundPopupOpen = function () {
        try { self._dialog.showModal(); }
        catch (e) { self._dialog.setAttribute('open', ''); Mg.debug('Quiz:popup:open', e); }
        // Focus the first answer for keyboard users.
        var firstAnswer = self.querySelector('[data-mg-quiz-step]:not([hidden]) [data-mg-quiz-answer] input');
        if (firstAnswer) firstAnswer.focus({ preventScroll: true });
      };

      this._boundPopupClose = function () {
        try { self._dialog.close(); }
        catch (e) { self._dialog.removeAttribute('open'); Mg.debug('Quiz:popup:close', e); }
      };

      // Click outside (on dialog backdrop) closes — native pattern.
      this._boundPopupBackdrop = function (e) {
        if (e.target === self._dialog) self._boundPopupClose();
      };

      this._popupTrigger = trigger;
      this._popupCloseBtn = closeBtn;
      if (trigger) trigger.addEventListener('click', this._boundPopupOpen);
      if (closeBtn) closeBtn.addEventListener('click', this._boundPopupClose);
      this._dialog.addEventListener('click', this._boundPopupBackdrop);

      // Public open method — lets an EXTERNAL trigger (e.g. a megamenu
      // teaser via the global [data-mg-quiz-open] listener below) open this
      // quiz's dialog by reusing the exact same open logic (showModal + focus).
      // Only defined in popup mode (no dialog ⇒ early return above).
      this.openPopup = this._boundPopupOpen;
    }

    _attachExternalListeners() {
      var self = this;

    // Bidirectional cart event listener (anti-loop via _source filter)
      this._boundCartUpdate = function (e) {
        if (e.detail && e.detail._source === 'mg-quiz') return;
        // Other components mutated cart (e.g. drawer remove) — no UI refresh needed for quiz itself
      };
      document.addEventListener('namy:cart:updated', this._boundCartUpdate);

      // Theme Editor: jump to the question step the merchant just clicked
      this._boundEditorSelect = function (e) {
        if (!self.contains(e.target)) return;
        var step = e.target.closest('[data-question-id]');
        if (step) self._goToStep(self.questionSteps.indexOf(step));
      };
      document.addEventListener('shopify:block:select', this._boundEditorSelect);
    }

    /* -----------------------------------------------------------------
       Selection + navigation
       ----------------------------------------------------------------- */

    _selectAnswer(questionId, answerEl) {
      var answerId = answerEl.dataset.answerId;
      var reasoningPhrase = answerEl.dataset.reasoning || '';
      var matchReasonPhrase = answerEl.dataset.matchReason || '';
      var productHandles = (answerEl.dataset.products || '').split(',').filter(Boolean);
      var score = parseInt(answerEl.dataset.score, 10) || 0;
      var skipTo = answerEl.dataset.skipTo || 'next';

      this.state.answers[questionId] = {
        answerId: answerId,
        reasoningPhrase: reasoningPhrase,
        matchReasonPhrase: matchReasonPhrase,
        productHandles: productHandles,
        score: score,
        skipTo: skipTo
      };
      this._persistDebounced();

      // Toggle selected styles
      var step = this.querySelector('[data-question-id="' + questionId + '"]');
      if (step) {
        step.querySelectorAll('[data-mg-quiz-answer]').forEach(function (el) {
          el.classList.toggle('is-selected', el.dataset.answerId === answerId);
        });
      }

      MgEvents.publish(MgEvents.Events.QUIZ_ANSWER_SELECTED, {
        runId: this.state.runId, questionId: questionId, answerId: answerId
      });

      // Auto-advance is always on for single-select questions (UX best
      // practice — saves a tap; multi-select questions would naturally not
      // auto-advance, but this v1 only ships single-select).
      var self = this;
      self._autoAdvanceTimer = setTimeout(function () { self._next(); }, 220);
    }

    _next(allowSkip) {
      var currentQuestion = this.questionSteps[this.state.currentStep];
      var qid = currentQuestion && currentQuestion.dataset.questionId;
      var required = currentQuestion && currentQuestion.dataset.required === 'true';
      var hasAnswer = qid && this.state.answers[qid];

      if (!allowSkip && required && !hasAnswer) {
        currentQuestion.classList.add('is-invalid');
        return;
      }

      MgEvents.publish(MgEvents.Events.QUIZ_STEP_VIEWED, {
        runId: this.state.runId, step: this.state.currentStep + 1
      });

      // Conditional branching: if the selected answer carries skipTo='result',
      // bypass remaining questions and resolve the recommendation immediately.
      // Default 'next' = standard linear flow.
      var currentAnswer = qid && this.state.answers[qid];
      if (currentAnswer && currentAnswer.skipTo === 'result') {
        this._complete();
        return;
      }

      if (this.state.currentStep < this.questionSteps.length - 1) {
        this._goToStep(this.state.currentStep + 1);
      } else {
        this._complete();
      }
    }

    _prev() {
      if (this.state.currentStep > 0) this._goToStep(this.state.currentStep - 1);
    }

    _goToStep(index) {
      this.state.currentStep = index;
      this._render();
      this._persistDebounced();
      var step = this.questionSteps[index];
      if (step) {
        var firstFocus = step.querySelector('[data-mg-quiz-answer]');
        if (firstFocus) firstFocus.focus({ preventScroll: false });
      }
    }

    /* -----------------------------------------------------------------
       Render
       ----------------------------------------------------------------- */

    _render() {
      var current = this.state.currentStep;
      var total = this.questionSteps.length;
      var showingResult = !!this.state.showingResult;

      this.questionSteps.forEach(function (step, i) {
        if (showingResult) {
          step.hidden = true;
        } else {
          step.hidden = i !== current;
        }
      });

      if (this.resultStep) this.resultStep.hidden = !showingResult;

      // Progress
      var progressEl = this.querySelector('[data-quiz-progress]');
      if (progressEl) {
        progressEl.hidden = showingResult;
        var pct = total > 0 ? Math.round(((current + (showingResult ? 1 : 0)) / total) * 100) : 0;
        progressEl.setAttribute('aria-valuenow', String(pct));
        var fill = this.querySelector('[data-quiz-progress-fill]');
        // A ratio as well as a percentage: the bar scales on the compositor
        // instead of animating its width, which would relayout every frame.
        if (fill) {
          fill.style.setProperty('--progress', pct + '%');
          fill.style.setProperty('--progress-ratio', String(pct / 100));
        }
        var text = this.querySelector('[data-quiz-progress-text]');
        if (text && this.locale.stepOf) {
          text.textContent = this.locale.stepOf
            .replace('{{ current }}', String(current + 1))
            .replace('{{ total }}', String(total));
        }
      }

      // Nav visibility
      this._updateNav();
    }

    _updateNav() {
      var prevBtn = this.querySelector('[data-quiz-prev]');
      var nextBtn = this.querySelector('[data-quiz-next]');
      var skipBtn = this.querySelector('[data-quiz-skip]');
      var current = this.state.currentStep;
      var total = this.questionSteps.length;
      var showingResult = !!this.state.showingResult;
      var currentQuestion = this.questionSteps[current];
      // allow-back is always on (a11y + user agency); auto-advance always on
      // (so next button only shows on final step where user might want to
      // submit without picking — but we hide it because last step auto-completes).
      if (prevBtn) prevBtn.hidden = showingResult || current === 0;
      if (nextBtn) nextBtn.hidden = showingResult || current < total - 1;
      if (skipBtn) skipBtn.hidden = true;

      if (nextBtn && current === total - 1) {
        nextBtn.textContent = this.locale.submit || nextBtn.textContent;
      }
    }

    /* -----------------------------------------------------------------
       Completion + result resolution (direct mode v1)
       ----------------------------------------------------------------- */

    _complete() {
      var matchHandle = this._resolveMatch();
      this.state.showingResult = true;
      this.state.matchHandle = matchHandle;

      this._renderReasoning();
      this._renderBullets();
      this._renderHero(matchHandle);
      this._renderAlt(this.state.altHandle);
      this._render();
      this._persistDebounced();

 // stable cross-section channel (producer contract). Consumers like
      // mg-routine-builder read this single key instead of reaching into the
      // per-section namy:quiz:<id>:state internals. Additive + best-effort.
      try {
        localStorage.setItem('namy:quiz:last-result', JSON.stringify({
          matchHandle: matchHandle, runId: this.state.runId, ts: Date.now()
        }));
      } catch (e) { Mg.debug && Mg.debug('Quiz:lastResult', e); }

      MgEvents.publish(MgEvents.Events.QUIZ_COMPLETED, {
        runId: this.state.runId, matchHandle: matchHandle
      });
      MgEvents.track('quiz:completed', { run_id: this.state.runId, match_handle: matchHandle });
      MgEvents.publish(MgEvents.Events.QUIZ_RESULT_VIEWED, {
        runId: this.state.runId, matchHandle: matchHandle
      });

      // Email gate orchestration (post-Q5 refactor):
      // - 'before' = required-style: hide hero until user provides email or skips
      // - 'after'  = collapsed by default, user opts in via tertiary "Send by
      //              email" text-link (premium 2026 progressive-disclosure)
      // - 'none'   = no email capture
      var pos = this.dataset.emailPosition || (this.config && this.config.emailPosition) || 'none';
      if (pos === 'before') {
        var hero = this.querySelector('[data-quiz-hero]');
        if (hero) hero.hidden = true;
        this._showEmailGate();
      }
      // 'after' mode: gate stays collapsed; tertiary link toggles it (less
      // friction, cleaner first-look at the recommendation).
    }

    _resolveMatch() {
      // Direct mode: count occurrences across all selected answers' productHandles.
      // Score mode: weight each occurrence by the answer's score_weight setting.
      // Mode is auto-derived (set in connectedCallback): 'score' if any answer has
      // score_weight != 1, otherwise 'direct'. Tie-break in both: first-seen wins.
      // Fallback: merchant-configured handle returned when no path produces a match.
      var mode = this._resolutionMode || 'direct';
      var weights = {};
      var firstSeen = {};
      var idx = 0;
      Object.keys(this.state.answers).forEach(function (qid) {
        var a = this.state.answers[qid];
        var weight = mode === 'score' ? Math.max(1, Number(a.score) || 1) : 1;
        (a.productHandles || []).forEach(function (h) {
          if (!h) return;
          weights[h] = (weights[h] || 0) + weight;
          if (firstSeen[h] === undefined) { firstSeen[h] = idx++; }
        });
      }, this);

      var best = null, bestWeight = 0;
      Object.keys(weights).forEach(function (h) {
        if (weights[h] > bestWeight || (weights[h] === bestWeight && firstSeen[h] < firstSeen[best])) {
          best = h; bestWeight = weights[h];
        }
      });

      // Fallback to merchant-configured product when no per-answer match
      var fallback = this.dataset.fallbackHandle;
      if (!best && fallback) best = fallback;

      // Alt match = next best (different from primary)
      this.state.altHandle = null;
      if (mode === 'score') {
        var sorted = Object.keys(weights).sort(function (a, b) {
          if (weights[b] !== weights[a]) return weights[b] - weights[a];
          return firstSeen[a] - firstSeen[b];
        });
        if (sorted.length > 1 && sorted[1] !== best) this.state.altHandle = sorted[1];
      }

      return best;
    }

    _renderReasoning() {
      var el = this.querySelector('[data-quiz-reasoning]');
      if (!el) return;
      var phrases = Object.keys(this.state.answers)
        .map(function (qid) { return this.state.answers[qid].reasoningPhrase; }, this)
        .filter(Boolean)
        .slice(0, 3);

      // Wrap each phrase in <strong> for emphasis on the user's exact words —
      // this is the qualitative driver of "felt heard" per the 2026 community
      // research synthesis. Phrases are merchant-controlled text already; we
      // escape via Mg.escapeHtml then add the <strong> wrap.
      var self = this;
      var wrapped = phrases.map(function (p) { return '<strong>' + Mg.escapeHtml(p) + '</strong>'; });

      var intro = '';
      if (wrapped.length === 3 && this.locale.reasoningIntro3) {
        intro = this.locale.reasoningIntro3
          .replace('{q1}', wrapped[0]).replace('{q2}', wrapped[1]).replace('{q3}', wrapped[2]);
      } else if (wrapped.length === 2 && this.locale.reasoningIntro2) {
        intro = this.locale.reasoningIntro2.replace('{q1}', wrapped[0]).replace('{q2}', wrapped[1]);
      } else if (wrapped.length === 1 && this.locale.reasoningIntro1) {
        intro = this.locale.reasoningIntro1.replace('{q1}', wrapped[0]);
      }

      // Strip trailing comma + finalize with period when reasoning_match is
      // not appended (matchHandle null = locale's chained "we picked X" never fires).
      intro = intro.replace(/[,\s]+$/, '.');

      el.innerHTML = intro;
    }

    _renderBullets() {
      var ul = this.querySelector('[data-quiz-bullets]');
      if (!ul) return;
      ul.innerHTML = '';
      var bullets = Object.keys(this.state.answers)
        .map(function (qid) { return this.state.answers[qid].matchReasonPhrase; }, this)
        .filter(Boolean)
        .slice(0, 3);
      bullets.forEach(function (text) {
        var li = document.createElement('li');
        li.className = 'mg-quiz__why-item';
        li.textContent = text;
        ul.appendChild(li);
      });
    }

    _renderHero(handle) {
      var hero = this.querySelector('[data-quiz-hero]');
      var heroImage = this.querySelector('[data-quiz-hero-image]');
      var heroMeta = this.querySelector('[data-quiz-hero-meta]');
      var atcBtn = this.querySelector('[data-quiz-atc]');
      var atcLabelEl = this.querySelector('[data-quiz-atc-label]');
      var resultEl = this.querySelector('[data-quiz-result]');
      var ctaMode = (resultEl && resultEl.dataset.ctaMode) || 'atc';
      if (!hero) return;

      if (!handle) {
        hero.hidden = true;
        if (atcBtn) atcBtn.hidden = true;
        return;
      }

      // Swap CTA label based on mode (atc vs view_product). Locale strings
      // come pre-translated from the JSON config bridge.
      if (atcLabelEl) {
        if (ctaMode === 'view_product' && this.locale.ctaViewProduct) {
          atcLabelEl.textContent = this.locale.ctaViewProduct;
        } else if (this.locale.ctaAtc) {
          atcLabelEl.textContent = this.locale.ctaAtc;
        }
      }

      // Fetch product card HTML via Section Rendering API.
      // Mg.section.fetch wraps the network + DOMParser logic — DRY win.
      var self = this;
      var seq = self._heroSeq = (self._heroSeq || 0) + 1;
      var url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'products/' + handle
        : '/products/' + handle;

      fetch(url + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) {
          if (self._heroSeq !== seq) return;
          if (!product) return;
          self.state.matchVariantId = product.variants && product.variants[0] && product.variants[0].id;
          self.state.matchTitle = product.title;
          self._buildHeroImage(heroImage, product);
          self._buildHeroMeta(heroMeta, product);
          hero.hidden = false;
          if (atcBtn) atcBtn.hidden = false;
        })
        .catch(function (e) { Mg.debug('Quiz:fetchProduct', e); });
    }

    _renderAlt(altHandle) {
      var altWrapper = this.querySelector('[data-quiz-alt-wrapper]');
      var altSlot = this.querySelector('[data-quiz-alt]');
      if (!altWrapper || !altSlot) return;
      if (!altHandle) { altWrapper.hidden = true; return; }

      var self = this;
      var seq = self._altSeq = (self._altSeq || 0) + 1;
      var url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root + 'products/' + altHandle
        : '/products/' + altHandle;
      fetch(url + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) {
          if (self._altSeq !== seq) return;
          if (!product) { altWrapper.hidden = true; return; }
          self._buildHeroCard(altSlot, product);
          altWrapper.hidden = false;
        })
        .catch(function (e) { Mg.debug('Quiz:fetchAlt', e); });
    }

    _buildHeroImage(slot, product) {
      if (!slot) return;
      var safeTitle = Mg.escapeHtml(product.title || '');
      // Demo mode OVERRIDES the recommended product image with the demo product (#1).
      var img = this.dataset.demoImage || product.featured_image || (product.images && product.images[0]) || '';
      var url = product.url || '#';
      slot.innerHTML = ''
        + '<a class="mg-quiz__hero-image-link" href="' + url + '" aria-label="' + safeTitle + '">'
        + (img ? '<img class="mg-quiz__hero-img" src="' + img + '" alt="' + safeTitle + '" loading="lazy" width="800" height="800">' : '')
        + '</a>';
    }

    _buildHeroMeta(slot, product) {
      if (!slot) return;
      var safeTitle = Mg.escapeHtml(product.title || '');
      var price = Mg.formatMoney(product.price);
      var compareAt = product.compare_at_price && product.compare_at_price > product.price
        ? Mg.formatMoney(product.compare_at_price) : null;
      var url = product.url || '#';
      slot.innerHTML = ''
        + '<h3 class="mg-quiz__hero-title"><a href="' + url + '">' + safeTitle + '</a></h3>'
        + '<p class="mg-quiz__hero-price">'
        + (compareAt ? '<s class="mg-quiz__hero-price-compare">' + compareAt + '</s> ' : '')
        + '<span class="mg-quiz__hero-price-final">' + price + '</span>'
        + '</p>';
    }

    _buildHeroCard(hero, product) {
      // Legacy method kept for _renderAlt (alt match still uses card layout).
      var safeTitle = Mg.escapeHtml(product.title || '');
      var img = product.featured_image || (product.images && product.images[0]) || '';
      var price = Mg.formatMoney(product.price);
      var compareAt = product.compare_at_price && product.compare_at_price > product.price
        ? Mg.formatMoney(product.compare_at_price) : null;
      var url = product.url || '#';

      var html = ''
        + '<article class="mg-product-card mg-quiz__hero-card">'
        + '<a class="mg-product-card__media-wrapper" href="' + url + '" aria-label="' + safeTitle + '">'
        + (img ? '<img class="mg-product-card__media" src="' + img + '" alt="' + safeTitle + '" loading="lazy" width="600" height="800">' : '')
        + '</a>'
        + '<div class="mg-product-card__info">'
        + '<h3 class="mg-product-card__title"><a href="' + url + '">' + safeTitle + '</a></h3>'
        + '<p class="mg-product-card__price">'
        + (compareAt ? '<s class="mg-product-card__price-compare">' + compareAt + '</s> ' : '')
        + '<span class="mg-product-card__price-final">' + price + '</span>'
        + '</p>'
        + '</div>'
        + '</article>';
      hero.innerHTML = html;
    }

    /* -----------------------------------------------------------------
       Email step (when emailPosition = 'before' | 'after')
       ----------------------------------------------------------------- */

    _attachEmailHandlers() {
      var self = this;
      var wrap = this.querySelector('[data-quiz-email-form]');
      if (!wrap) return;
      var form = wrap.querySelector('form');
      var noteInput = wrap.querySelector('[data-quiz-email-note]');
      var toggleBtn = this.querySelector('[data-quiz-email-toggle]');

      if (form) {
        form.addEventListener('submit', function () {
          // Build note payload (run id + match + answer summary) — merchant
          // can read this in admin Customers note field, or via Klaviyo
          // "Submitted Form" event for follow-up flows.
          if (noteInput) {
            var summary = {
              runId: self.state.runId,
              match: self.state.matchHandle || '',
              answers: Object.keys(self.state.answers).map(function (qid) {
                return self.state.answers[qid].reasoningPhrase;
              }).filter(Boolean)
            };
            try { noteInput.value = JSON.stringify(summary); } catch (e) { Mg.debug('Quiz:emailNote', e); }
          }
          MgEvents.publish(MgEvents.Events.QUIZ_EMAIL_CAPTURED, { runId: self.state.runId });
          MgEvents.track('quiz:email:captured', { run_id: self.state.runId });
          // Form submits natively (POST to /contact) — Shopify handles redirect / success
        });
      }

      // Tertiary text-link toggle: clicking "Send by email" expands the
      // collapsed email gate inline. Same selector is used by the click
      // handler — the link IS the toggle. Premium 2026 pattern: progressive
      // disclosure replacing always-visible secondary form.
      if (toggleBtn) {
        toggleBtn.addEventListener('click', function () {
          wrap.hidden = !wrap.hidden;
          toggleBtn.setAttribute('aria-expanded', String(!wrap.hidden));
          if (!wrap.hidden) {
            var input = wrap.querySelector('input[type="email"]');
            if (input) input.focus({ preventScroll: false });
          }
        });
        toggleBtn.setAttribute('aria-expanded', 'false');
        toggleBtn.setAttribute('aria-controls', wrap.id || 'mg-quiz-email-form');
      }
    }

    _showEmailGate() {
      var wrap = this.querySelector('[data-quiz-email-form]');
      if (!wrap) { this._showResult(); return; }
      wrap.hidden = false;
    }

    _showResult() {
      var wrap = this.querySelector('[data-quiz-email-form]');
      if (wrap) wrap.hidden = true;
      // Result body already rendered in _complete — just ensure it's visible
      var hero = this.querySelector('[data-quiz-hero]');
      if (hero && this.state.matchHandle) hero.hidden = false;
    }

    /* -----------------------------------------------------------------
       CTA dispatcher — atc (default) | view_product (link to PDP)
       Mode is read from the result block's data-cta-mode attribute,
       set by the merchant via the result block's "Result CTA" select.
       ----------------------------------------------------------------- */

    _handleCta() {
      var resultEl = this.querySelector('[data-quiz-result]');
      var mode = (resultEl && resultEl.dataset.ctaMode) || 'atc';
      if (mode === 'view_product') {
        this._navigateToProduct();
      } else {
        this._atc();
      }
    }

    _navigateToProduct() {
      var handle = this.state.matchHandle;
      if (!handle) return;
      var url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root + 'products/' + handle
        : '/products/' + handle;
      MgEvents.publish(MgEvents.Events.QUIZ_RESULT_ATC, {
        runId: this.state.runId, matchHandle: handle, mode: 'view_product'
      });
      MgEvents.track('quiz:result:atc', {
        run_id: this.state.runId, match_handle: handle, mode: 'view_product'
      });
      window.location.href = url;
    }

    /* -----------------------------------------------------------------
       ATC chain — Mg.cart.add + Mg.cart.update + dispatch
       ----------------------------------------------------------------- */

    _atc() {
      var variantId = this.state.matchVariantId;
      if (!variantId) return;

      var self = this;
      var items = [{
        id: variantId,
        quantity: 1,
        properties: { _quiz_run_id: this.state.runId }   // _ prefix hides from order email
      }];
      var sections = ['mg-cart-drawer', 'mg-cart-icon-bubble'];

      // Multi-instance / Rollouts compat: namespace cart attributes by sectionId
      // so two quiz sections on the same page (variant A vs variant B) write
      // distinct keys. Generic legacy keys preserved for backward compat with
      // single-instance reporters / Klaviyo flows configured pre-Q5.
      var sectionId = self.sectionId;
      var attrs = {
        namy_quiz_run_id: self.state.runId,
        namy_quiz_match: self.state.matchHandle || ''
      };
      attrs['namy_quiz_' + sectionId + '_run_id'] = self.state.runId;
      attrs['namy_quiz_' + sectionId + '_match'] = self.state.matchHandle || '';

      Mg.cart.add(items, sections, { source: 'mg-quiz', announce: false })
        .then(function () {
          // One announcement for the pair, tagged so the quiz skips its own echo.
          return Mg.cart.update({ attributes: attrs }, [], { source: 'mg-quiz' });
        })
        .then(function (cart) {
          if (!cart) return;
          MgEvents.publish(MgEvents.Events.CART_ITEM_ADDED, {
            variant_id: variantId, source: 'quiz'
          });
          /* Only the drawer listens to that event. Without one, confirm the
             add here rather than leave the result step looking inert. */
          if (!document.querySelector('mg-cart-drawer') && window.MgToast) {
            MgToast.show(self.dataset.addedMessage || 'Added to cart', 'success');
          }
          MgEvents.publish(MgEvents.Events.QUIZ_RESULT_ATC, {
            runId: self.state.runId, variantId: variantId, matchHandle: self.state.matchHandle
          });
          MgEvents.track('quiz:result:atc', {
            run_id: self.state.runId, variant_id: variantId
          });
        })
        .catch(function (e) { Mg.debug('Quiz:atc', e); });
    }

    /* -----------------------------------------------------------------
       Misc actions
       ----------------------------------------------------------------- */

    _retake() {
      try { localStorage.removeItem(this._stateKey()); } catch (e) { Mg.debug('Quiz:retakeClear', e); }
      this.state = this._freshState();
      // Clear selected styles
      this.querySelectorAll('[data-mg-quiz-answer].is-selected').forEach(function (el) {
        el.classList.remove('is-selected');
      });
      this.querySelectorAll('input[type="radio"]:checked').forEach(function (input) { input.checked = false; });
      this._render();
    }

    _share() {
      var self = this;
      var url = window.location.href.split('#')[0] + '#quiz=' + this.state.runId;
      var done = function () {
        var btn = self.querySelector('[data-quiz-share]');
        if (btn && self.locale.shareCopied) {
          var prev = btn.textContent;
          btn.textContent = self.locale.shareCopied;
          setTimeout(function () { btn.textContent = prev; }, 1500);
        }
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(function (e) { Mg.debug('Quiz:share', e); });
      } else {
        done();
      }
    }
  }

  customElements.define('mg-quiz', MgQuiz);
}

/* -------------------------------------------------------------------------
   Global open-by-handle — decoupled trigger.
   Lets any [data-mg-quiz-open="<handle>"] element (e.g. a megamenu quiz
   teaser, rendered far from the quiz section) open a popup quiz dialog
   anywhere on the page. The dialog is the source of truth (data-quiz-handle);
   the trigger holds no quiz logic. Section-scoped triggers (data-mg-quiz-
   trigger) are unaffected — this is purely additive.

   Page-lifetime singleton (one delegated listener, idempotent) — matches the
   established global-listener pattern (cf. ATC label-flip singleton). The
   <mg-quiz> engine is loaded on every page (Tier-1 bundle), so this is too.
   ------------------------------------------------------------------------- */
if (!window.__namyQuizOpenInit) {
  window.__namyQuizOpenInit = true;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-mg-quiz-open]');
    if (!btn) return;
    try {
      var handle = btn.getAttribute('data-mg-quiz-open');
      var sel = handle
        ? '.mg-quiz__dialog[data-quiz-handle="' + (window.CSS && CSS.escape ? CSS.escape(handle) : handle) + '"]'
        : '.mg-quiz__dialog';
      var dialog = document.querySelector(sel);
      if (!dialog) return;
      var engine = dialog.querySelector('mg-quiz');
      if (engine && typeof engine.openPopup === 'function') {
        engine.openPopup();
      } else if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      }
    } catch (err) {
      if (window.Mg && Mg.debug) Mg.debug('Quiz:globalOpen', err);
    }
  });
}
